import React from 'react'
import isEmpty from 'lodash/isEmpty'
import {
  Box,
  Typography,
  IconButton,
  MenuItem,
  Menu,
  Grid,
} from '@the-platform-group/component-library'
import { formatDateTimeToLocalDate } from 'utils/format'
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
} from '@the-platform-group/component-library/Icons'
import { TruncatedText } from 'components/TruncatedText'
import { StatusPill } from '@the-platform-group/component-library'
import { IRWIN_ASSISTANT_STATUS_TO_STATUS_PILL_STATUS } from '@the-platform-group/component-library/IrwinAssistant'
import FilesCell from '../components/FilesCell'
import { getBulkDownloadUrl } from 'services/userUploadRequests'
import { humanizeSnakeCase } from 'utils/format'
import { PRIORITY_OPTIONS } from './constants'
import { titleCase } from 'change-object-case'
import { formatDate } from '@the-platform-group/formatters/dateFormat'
import { TABLE_DATE_FORMAT } from 'constants/formats'
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@the-platform-group/component-library/DataGrid'

function getColumnValues({
  currentUser,
  handleFileDownload,
  handleManageModalOpen,
  handleEditModalOpen,
  handleActionLogin,
  handleDeleteModalOpen,
}) {
  return [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      headerName: '',
      minWidth: 40,
      width: 40,
      disableColumnMenu: true,
      flex: null,
      resizable: false,
    },
    { field: 'customer', valueGetter: ({ value: customer }) => customer.name },
    {
      field: 'security',
      width: 200,
      renderCell: ({ value }) => (
        <Typography variant="body2">{value?.tickerExchange || '-'}</Typography>
      ),
    },
    { field: 'userName' },
    { field: 'userEmail' },
    {
      field: 'requestName',
      width: 180,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight="Medium">
          {value}
        </Typography>
      ),
    },
    {
      field: 'description',
      width: 280,
      flex: null,
      renderCell: ({ value }) => (
        <Box width="100%">
          <TruncatedText title="" content={value} contentPreview={value} maxLines={2} />
        </Box>
      ),
    },
    {
      field: 'files',
      width: 280,
      valueGetter: ({ row }) => row,
      renderCell: ({ value }) => {
        if (isEmpty(value.filePaths)) {
          return '-'
        }

        return (
          <FilesCell
            value={value || {}}
            handleFileDownload={handleFileDownload}
            getBulkDownloadUrl={getBulkDownloadUrl}
          />
        )
      },
    },
    {
      field: 'requestedAt',
      headerName: 'Request Date',
      sortable: true,
      renderCell: ({ value }) => {
        if (!value) {
          return '-'
        }
        const localDate = formatDateTimeToLocalDate(value)
        return formatDate(localDate, TABLE_DATE_FORMAT)
      },
    },
    {
      field: 'assignee',
      renderCell: ({ value }) => {
        if (!value?.id) {
          return <Typography variant="subtitle">-</Typography>
        }

        return (
          <Grid container direction="column">
            <Box>
              <PersonIcon sx={{ mr: 1, fontSize: 15 }} />
              <Typography variant="subtitle">
                {value.firstName} {value.lastName}
              </Typography>
            </Box>
          </Grid>
        )
      },
    },
    {
      field: 'internalComments',
      width: 280,
      flex: null,
      renderCell: ({ value }) => (
        <Box width="100%">
          <TruncatedText title="" content={value} contentPreview={value} maxLines={2} />
        </Box>
      ),
    },
    {
      field: 'status',
      width: 140,
      renderCell: ({ value }) =>
        value ? (
          <StatusPill
            status={IRWIN_ASSISTANT_STATUS_TO_STATUS_PILL_STATUS[value]}
            text={humanizeSnakeCase(value)}
          />
        ) : (
          '-'
        ),
    },
    {
      field: 'priority',
      width: 120,
      renderCell: ({ value }) => {
        const option = PRIORITY_OPTIONS[value]

        return value ? (
          <Typography
            variant="body2"
            sx={{
              color: option.color,
              backgroundColor: option.backgroundColor,
              borderRadius: 1,
              py: 0.25,
              px: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {option.icon && React.createElement(option.icon)}
            {titleCase(value)}
          </Typography>
        ) : (
          '-'
        )
      },
    },
    {
      field: 'expectedDate',
      width: 140,
      sortable: true,
      renderCell: ({ value }) => {
        if (!value) {
          return '-'
        }

        const localDate = formatDateTimeToLocalDate(value)
        const color = localDate < new Date() ? 'error.main' : 'default.main'

        return (
          <Typography variant="body" color={color}>
            {formatDate(localDate, TABLE_DATE_FORMAT)}
          </Typography>
        )
      },
    },
    {
      field: 'customerComments',
      headerName: 'Customer Facing Notes',
      width: 280,
      flex: null,
      renderCell: ({ value }) => (
        <Box width="100%">
          <TruncatedText title="" content={value || ''} contentPreview={value || ''} maxLines={2} />
        </Box>
      ),
    },
    {
      field: 'actions',
      width: 80,
      type: 'actions',
      getActions: ({ row }) => [
        <ActionsMenu
          key={`actions-${row.id}`}
          row={row}
          handleManageModalOpen={handleManageModalOpen}
          handleEditModalOpen={handleEditModalOpen}
          handleActionLogin={handleActionLogin}
          handleDeleteModalOpen={handleDeleteModalOpen}
        />,
      ],
    },
  ]
}

// ActionsMenu component to handle dropdown menu
const ActionsMenu = ({
  row,
  handleManageModalOpen,
  handleEditModalOpen,
  handleActionLogin,
  handleDeleteModalOpen,
}) => {
  // this is needed to make the menu close when an item is clicked
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = action => {
    action(row)
    handleClose()
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-controls={open ? 'actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick(handleManageModalOpen)}>
          <Typography>Manage request</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(handleEditModalOpen)}>
          <Typography>Edit request</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(handleActionLogin)}>
          <Typography>Irwin assistant login</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(handleDeleteModalOpen)}>
          <Typography>Delete request</Typography>
        </MenuItem>
      </Menu>
    </>
  )
}

const formatRowValues = uploadRequests =>
  uploadRequests.map(uploadRequest => {
    const {
      id,
      customer,
      user,
      message,
      filePaths,
      createdAt,
      requestedAt,
      status,
      assignee,
      internalComments,
      security,
      name,
      priority,
      dueAt,
      customerComments,
      adminCreated,
    } = uploadRequest
    const { firstName, lastName, email } = user

    return {
      id,
      customer,
      userName: `${firstName} ${lastName}`,
      userEmail: email,
      security,
      requestName: name,
      description: message,
      fileCount: filePaths?.length || 0,
      filePaths,
      requestedAt,
      createdAt,
      priority,
      expectedDate: dueAt,
      status,
      assignee,
      customerComments,
      internalComments,
      user,
      adminCreated,
    }
  })

export { getColumnValues, formatRowValues }
